<template>
  <div class="w-full flex justify-center pb-16 px-4 sm:px-8 mt-2">
    <div class="w-full max-w-4xl">
      
      <!-- HEADER (Strictly Left Aligned) -->
      <div class="flex flex-col mb-10 pb-6 border-b border-gray-100 text-left w-full items-start animate-fade-in">
        <h1 class="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight mb-3">
          {{ $t("searchCompetency.sc1") }}
        </h1>
        <p class="text-gray-500 font-medium text-lg">{{ $t("searchCompetency.sc2") }}</p>
      </div>

      <!-- MODERN SEARCH BAR -->
      <div class="mb-10 w-full animate-fade-in" style="animation-delay: 0.1s;">
        <div class="bg-white border border-gray-200 shadow-sm transition-all duration-300 hover:shadow-md hover:border-gray-300 search-wrapper relative flex items-center px-2">
          
          <!-- Custom Search Icon -->
          <div class="pl-4 pr-2 text-primary">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
          </div>

          <!-- Overriding Vuesax Input -->
          <vs-input
            class="w-full modern-search-input"
            v-model="searchInput"
            placeholder="Search attributes by name or description..."
            icon-no-border
          />
        </div>
      </div>

      <!-- COMPETENCY CARDS LIST -->
      <div class="flex flex-col space-y-4 animate-fade-in-up" style="animation-delay: 0.2s;">
        <div
          v-for="comp in relevantCompetencies"
          :key="comp.id"
          class="flex items-center p-5 sm:p-6 cursor-pointer select-none relative overflow-hidden group comp-card border transition-all duration-300"
          :class="$store.state.addSkill.competency.id == comp.id 
            ? 'bg-primary border-primary shadow-lg -translate-y-1' 
            : 'bg-white border-gray-100 hover:border-gray-300 hover:shadow-md hover:-translate-y-1'"
          @click="storeCompetency(comp)"
          @dblclick.stop="doubleClickCompetency(comp)"
        >

          <!-- Icon Wrapper -->
          <div class="flex-shrink-0 mr-5 sm:mr-6 relative z-10">
            <span class="inline-flex items-center justify-center w-16 h-16 transition-colors duration-300 char-icon-wrapper" 
                  style="border-radius: 1rem;"
                  :class="$store.state.addSkill.competency.id == comp.id ? 'bg-white/20' : 'bg-gray-50 group-hover:bg-primary/10'">
              <img :src="comp.icon" alt="icon" width="32px" height="32px" 
                   class="transition-all duration-300 group-hover:scale-110" 
                   :class="{'brightness-0 invert': $store.state.addSkill.competency.id == comp.id, 'greyscale-icon': $store.state.addSkill.competency.id != comp.id}" />
            </span>
          </div>

          <!-- Text Content (Left Aligned) -->
          <div class="flex-grow text-left relative z-10 w-full overflow-hidden pr-4">
            <h4 class="font-extrabold text-xl mb-1 leading-tight transition-colors duration-300" 
                :class="$store.state.addSkill.competency.id == comp.id ? 'text-white' : 'text-gray-800 group-hover:text-primary'">
              {{ comp.comp }}
            </h4>
            <p class="text-sm leading-relaxed font-medium line-clamp-2 m-0 w-full transition-colors duration-300"
              :class="$store.state.addSkill.competency.id == comp.id ? 'text-white' : 'text-gray-500'">
              {{ comp.wdd }}
            </p>
          </div>

          <!-- Checkmark for Selected State -->
          <div class="flex-shrink-0 relative z-10 ml-2 transition-all duration-500" 
               :class="$store.state.addSkill.competency.id == comp.id ? 'opacity-100 translate-x-0 text-white' : 'opacity-0 translate-x-4 text-transparent'">
            <div class="p-2 rounded-full" :class="$store.state.addSkill.competency.id == comp.id ? 'bg-white/20' : ''">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
            </div>
          </div>

        </div>
        
        <!-- Empty State if search yields no results -->
        <div v-if="relevantCompetencies.length === 0" class="text-center p-10 bg-gray-50 rounded-3xl border border-dashed border-gray-200 mt-4">
          <p class="text-gray-500 font-medium text-lg">No attributes match your search.</p>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import i18n from "@/i18n/i18n";
export default {
  name: "SearchCompetency",
  data() {
    return {
      allCompetencies: [],
      CCD: {}, 
      show_competency_value: false,
      searchInput: null
    };
  },
  computed: {
    relevantCompetencies() {
      const allCompetencies = []
      for (const x in this.CCD) {
        for (const i in this.CCD[x].competencies) {
          allCompetencies.push({
            ...this.CCD[x].competencies[i],
            id: this.CCD[x].competencies[i].id
          });
        }
      }

      if (this.searchInput) {
        return allCompetencies.filter(item => {
          return this.searchInput
            .toLowerCase()
            .split(" ")
            .every(v => item.comp.toLowerCase().includes(v))
            || this.searchInput
              .toLowerCase()
              .split(" ")
              .every(v => item.wdd.toLowerCase().includes(v)) ;
        });
      }

      return allCompetencies
    }
  },
  watch: {
    relevantCompetencies: {
      deep: true,
      handler (newVal) {
        this.allCompetencies = newVal
      }
    }
  },
  methods: {
    doubleClickCompetency(comp) {
      this.storeCompetency(comp);
      this.$nextTick(() => {
        const nextButton = document.querySelector('.add-skill-step-back-next, button.vs-button-filled');
        if (nextButton && !nextButton.disabled) {
          nextButton.click();
        }
      });
    },
    storeCompetency(comp) {
      // -------------------------------------------------------------
      // NEW TOGGLE LOGIC: If already selected, UNSELECT it and stop.
      // -------------------------------------------------------------
      if (this.$store.state.addSkill.competency && this.$store.state.addSkill.competency.id === comp.id) {
        // Dispatch an empty object with a null id to clear the selection safely
        this.$store.dispatch("addSkill/setSelectedCompetency", { id: null }).catch(()=>{});
        // Clear the locked progress measures as well
        this.$store.commit("addSkill/SET_USED_PROGRESS_MEASURES", []);
        return; 
      }
      // -------------------------------------------------------------

      var url = new URL(window.location.href);
      let usersId = url.searchParams.get("userId") || this.$store.state.AppActiveUser.usersId;
      this.show_competency_value = true;

      this.$store
        .dispatch("addSkill/findCompetency", {query: {competencyId: comp.id, usersId}})
        .then(async (res) => {
          let usedPmIds = [];
          
          if(res.total > 0) {
            for (const record of res.data) {
              try {
                let pmRes = await this.$store.dispatch("trainee-competencies-prgms/find", { 
                  query: { trainee_competencies_id: record.id } 
                });
                
                if (pmRes && pmRes.data) {
                  pmRes.data.forEach(pm => {
                    usedPmIds.push(pm.progress_measure_id || pm.progressMeasureId || pm.prgms_id || pm.id);
                  });
                }
              } catch(err) {
                console.warn("Could not fetch old measures.", err);
              }
            }
          }

          // Save the used IDs to Vuex
          this.$store.commit("addSkill/SET_USED_PROGRESS_MEASURES", [...new Set(usedPmIds)]);

          // Proceed to next step
          this.$store
            .dispatch("addSkill/setSelectedCompetency", comp)
            .then(res => {
              console.log("selected competency", res);
            })
            .catch(err => {
              console.log("could not set selected competency", err);
            });
        })
        .catch(err => {
          console.log("error in finding competency", err)
        });
    }
  },
  created() {
    this.CCD = (this.$parent && this.$parent.CCD) ? this.$parent.CCD : this.$t("addNewSkill.data");

    for(const x in this.CCD) {
      for (const i in this.CCD[x].competencies) {
        this.allCompetencies.push({
          ...this.CCD[x].competencies[i],
          id: this.CCD[x].competencies[i].id.concat("-").concat(i)
        });
      }
    }
  }
};
</script>

<style scoped>
/* Base Animations */
.animate-fade-in {
  animation: fadeIn 0.4s ease-out forwards;
  opacity: 0;
}
.animate-fade-in-up {
  animation: fadeInDown 0.5s cubic-bezier(0.25, 1, 0.5, 1) forwards;
  opacity: 0;
}

@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}
@keyframes fadeInDown {
  0% { opacity: 0; transform: translateY(15px); }
  100% { opacity: 1; transform: translateY(0); }
}

/* ------------------------------------- 
   SEARCH BAR STYLING
   ------------------------------------- */
.search-wrapper {
  border-radius: 1.5rem !important; /* Forced pill/rounded shape */
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

/* Strip Vuesax default borders/backgrounds to integrate it into our custom wrapper */
.modern-search-input /deep/ .vs-inputx {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  font-size: 1.1rem;
  padding: 1rem 0.5rem;
  color: #374151;
  text-align: left !important;
}

.modern-search-input /deep/ .vs-inputx::placeholder {
  text-align: left !important;
}

.modern-search-input /deep/ .vs-inputx:focus {
  border: none !important;
  box-shadow: none !important;
}

.modern-search-input /deep/ .vs-icon {
  display: none !important;
}

.modern-search-input /deep/ .vs-con-input {
  text-align: left !important;
}

.modern-search-input /deep/ .vs-input--placeholder {
  text-align: left !important;
}

/* ------------------------------------- 
   COMPETENCY LIST CARDS
   ------------------------------------- */
.comp-card {
  border-radius: 1.5rem !important; /* Forced rounded corners */
}

.greyscale-icon { 
  mix-blend-mode: multiply; 
  opacity: 0.85; 
}

/* Image filtering trick to turn icons white when selected */
.brightness-0 { filter: brightness(0); }
.invert { filter: brightness(0) invert(1); }

/* Helper to clamp text to 2 lines beautifully */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;  
  overflow: hidden;
}
</style>